import { RefObject, useEffect } from 'react'

export const useDemoOverlayBounds = (containerRef: RefObject<HTMLDivElement>) => {
  useEffect(() => {
    const container = containerRef.current

    if (!container) {
      return
    }

    let frame: number | undefined
    const corrected = new WeakMap<HTMLElement, DemoOverlayPosition>()
    const constrain = () => {
      frame = undefined
      const bounds = container.getBoundingClientRect()
      const scale = bounds.width / container.offsetWidth || 1
      const left = Math.max(0, bounds.left) + 8
      const top = Math.max(0, bounds.top) + 8
      const right = Math.min(window.innerWidth, bounds.right) - 8
      const bottom = Math.min(window.innerHeight, bounds.bottom) - 8

      container.querySelectorAll<HTMLElement>('.MuiPopover-paper').forEach(paper => {
        const setStyle = ({ property, value }: DemoOverlayStyleParams) => {
          const pixels = `${Math.round(value * 100) / 100}px`

          if (paper.style[property] !== pixels) {
            paper.style[property] = pixels
          }
        }

        const previous = corrected.get(paper)
        const origin = getComputedStyle(paper).transformOrigin.split(' ').map(parseFloat)
        const freshPosition = !previous || previous.left !== paper.style.left || previous.top !== paper.style.top
        const logicalLeft = parseFloat(paper.style.left) || 0
        const logicalTop = parseFloat(paper.style.top) || 0
        // MUI writes viewport positions; the demo portal uses scaled local coordinates.
        const physicalLeft = freshPosition ? logicalLeft + (origin[0] || 0) * (1 - scale) : bounds.left + logicalLeft * scale
        const physicalTop = freshPosition ? logicalTop + (origin[1] || 0) * (1 - scale) : bounds.top + logicalTop * scale
        setStyle({ property: 'maxHeight', value: Math.max(0, bottom - top) / scale })
        setStyle({ property: 'maxWidth', value: Math.max(0, right - left) / scale })
        setStyle({ property: 'left', value: (Math.max(left, Math.min(physicalLeft, right - paper.offsetWidth * scale)) - bounds.left) / scale })
        setStyle({ property: 'top', value: (Math.max(top, Math.min(physicalTop, bottom - paper.offsetHeight * scale)) - bounds.top) / scale })
        corrected.set(paper, { left: paper.style.left, top: paper.style.top })
      })
    }
    const schedule = () => {
      if (frame === undefined) {
        frame = requestAnimationFrame(constrain)
      }
    }
    const observer = new MutationObserver(schedule)
    observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: [ 'style' ] })
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, true)
    schedule()

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule, true)

      if (frame !== undefined) {
        cancelAnimationFrame(frame)
      }
    }
  }, [ containerRef ])
}
